/**
 * Load a code-split chunk with resilience against transient CDN/network stalls
 * and stale post-deploy chunk maps.
 *
 * A dynamic import that HANGS (rather than rejecting) would otherwise leave a
 * route's <Suspense> fallback — the full-page brand loader — spinning forever
 * (the "/try loading forever" failure during a platform blip). A dynamic import
 * that REJECTS with "Failed to fetch dynamically imported module" is the classic
 * post-deploy case: a still-open old client asks for a chunk hash the new build
 * renamed. We race each attempt against a timeout, retry once, and as a last
 * resort force a single full reload (guarded against a loop), which re-requests
 * the chunk from a fresh document and picks up the new hashes.
 *
 * Shared by every code-split entry point — the routed pages (App.tsx) and the
 * Landing page's below-the-fold sections — so all dynamic imports get the same
 * recovery instead of leaking an uncaught rejection.
 */
export function loadChunkWithRetry<T>(loader: () => Promise<T>, timeoutMs = 10000): Promise<T> {
  const once = (): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error('chunk load timeout')), timeoutMs);
      loader().then(
        (m) => {
          clearTimeout(timer);
          resolve(m);
        },
        (e) => {
          clearTimeout(timer);
          reject(e);
        },
      );
    });
  return once()
    .catch(() =>
      // One quick retry — a fresh request often succeeds where the first stalled.
      new Promise((r) => setTimeout(r, 400)).then(once),
    )
    .catch((err) => {
      // Both attempts failed or stalled. A fresh document load re-fetches the
      // chunk (and picks up new hashes after a deploy). Reload once, guarded so
      // a genuinely broken chunk can't loop the page.
      try {
        const KEY = 'entredash.chunkReloadedAt';
        const last = Number(sessionStorage.getItem(KEY) || '0');
        if (Date.now() - last > 30000) {
          sessionStorage.setItem(KEY, String(Date.now()));
          window.location.reload();
          return new Promise<T>(() => {}); // reload takes over; never resolves
        }
      } catch {
        /* sessionStorage unavailable — fall through to surface the error */
      }
      throw err;
    });
}
